import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';
import { GalleryGroupRarityCounts } from 'shared/components/RarityCountsDisplay';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import {
  getBadgeCollectionGroup,
  setBadgeCollectionGroup,
  subscribeBadgeCollectionGroup,
} from 'features/catalog/badge-collection-group';
import { BADGE_GROUPS, groupBackgroundGradient, sortBadgeGroupsByGlobalClaimRate } from 'features/catalog/badge-groups';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { compareForceSortLast, compareBadgesByScoreDesc } from 'features/catalog/rarity';
import { talosBadgesLogoURL } from 'config/constants';
import './BadgeGalleryView.css';

interface BadgeGalleryViewProps {
  selectedProfileId?: string | null;
  onSelectBadge: (badge: Badge) => void;
  onSelectProfile: (profileId: string) => void;
  onClearProfile?: () => void;
}

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const BadgeGalleryView: React.FC<BadgeGalleryViewProps> = ({
  selectedProfileId = null,
  onSelectBadge,
  onSelectProfile,
  onClearProfile,
}) => {
  const { badgesMap } = useTalosData();
  const [activeGroup, setActiveGroupState] = useState(() => getBadgeCollectionGroup());
  const sectionRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  useEffect(() => subscribeBadgeCollectionGroup(() => setActiveGroupState(getBadgeCollectionGroup())), []);

  const setActiveGroup = (id: string) => {
    setBadgeCollectionGroup(id);
  };
  const { groups, freeform, totalCount } = useMemo(() => {
    const groupedIds = new Set<string>();
    const resolved = sortBadgeGroupsByGlobalClaimRate(BADGE_GROUPS, badgesMap)
      .map((group) => {
        const badges = group.badgesIDs
          .map((id) => {
            groupedIds.add(id);
            return badgesMap.get(id);
          })
          .filter((b): b is Badge => !!b)
          .sort((a, b) => compareForceSortLast(a, b) || compareBadgesByScoreDesc(a, b));
        return { ...group, badges, slug: slugify(group.name) };
      })
      .filter((group) => group.badges.length > 0);

    const freeformBadges = Array.from(badgesMap.values())
      .filter((badge) => !groupedIds.has(badge.id))
      .sort(compareBadgesByScoreDesc);

    return {
      groups: resolved,
      freeform: freeformBadges,
      totalCount: badgesMap.size,
    };
  }, [badgesMap]);

  const navItems = useMemo(() => {
    const items: { id: string; label: string; gradient?: string }[] = [
      { id: 'all', label: 'All' },
      ...groups.map((g) => ({
        id: g.slug,
        label: g.name,
        gradient: groupBackgroundGradient(g),
      })),
    ];
    if (freeform.length > 0) items.push({ id: 'freeform', label: 'More Badges' });
    return items;
  }, [groups, freeform.length]);

  // Drop profile-only pills (Badge Hash, Idea, Design, …) when entering the gallery.
  // Shared collection categories (Tenure, SRT Awards, etc.) stay selected.
  useEffect(() => {
    if (!navItems.some((item) => item.id === activeGroup)) {
      setBadgeCollectionGroup('all');
    }
  }, [navItems, activeGroup]);

  const selectGroup = (id: string) => {
    setActiveGroup(id);
    sectionRefs.current.get('gallery-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderGalleryBadge = (badge: Badge) => {
    const unreleased = !isBadgeReleased(badge.id);
    return (
      <div key={badge.id} className={`gallery-badge rarity-${badge.rarityName}`}>
        <BadgeComponent
          badge={badge}
          onClick={onSelectBadge}
          blackAndWhite={unreleased}
          lowOpacity={false}
          display
          smaller
          showName={!unreleased}
          hiddenName={unreleased}
          showNameHeight
          showFloatingNameShadow={false}
          cursorNotAllowed={unreleased}
        />
      </div>
    );
  };

  // Badge Hash / other profile-only filters are reset above; remaining ids are gallery-safe.
  const visibleGroups = activeGroup === 'all' ? groups : groups.filter((g) => g.slug === activeGroup);
  const showFreeform = (activeGroup === 'all' || activeGroup === 'freeform') && freeform.length > 0;

  return (
    <div className="gallery-page badges-gallery-page">
      <header
        className="gallery-header"
        ref={(el) => {
          sectionRefs.current.set('gallery-top', el);
        }}
      >
        <ProfileIdentitySearch
          selectedProfileId={selectedProfileId}
          onSelectProfile={onSelectProfile}
          onClear={onClearProfile}
          avatarSrc={talosBadgesLogoURL}
        >
          <h1 className="gallery-title">Badges Gallery</h1>
          <p className="gallery-subtitle">
            {totalCount} badges · {groups.length} collection
            {groups.length === 1 ? '' : 's'}
          </p>
        </ProfileIdentitySearch>
      </header>

      <div className="gallery-nav" role="tablist" aria-label="Badge collections">
        {navItems.map((item) => {
          const hasFill = Boolean(item.gradient);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={activeGroup === item.id}
              className={`gallery-nav-chip ${activeGroup === item.id ? 'active' : ''}${
                hasFill ? ' gallery-nav-chip--progress' : ''
              }`}
              onClick={() => selectGroup(item.id)}
            >
              {hasFill ? (
                <span
                  className="gallery-nav-chip-fill"
                  aria-hidden
                  style={{
                    background: item.gradient,
                    opacity: 0.22,
                  }}
                />
              ) : null}
              <span className="gallery-nav-chip-label">{item.label}</span>
            </button>
          );
        })}
      </div>

      <div className="gallery-sections">
        {visibleGroups.map((group) => (
          <section
            key={group.slug}
            id={`gallery-${group.slug}`}
            className="gallery-group"
            ref={(el) => {
              sectionRefs.current.set(group.slug, el);
            }}
          >
            <div className="gallery-group-fill" aria-hidden>
              <div
                className="gallery-group-fill-inner"
                style={{
                  background: groupBackgroundGradient(group),
                  opacity: 0.56,
                }}
              />
            </div>
            <div className="gallery-group-content">
              <header className="gallery-group-head">
                <span
                  className="gallery-group-swatch"
                  style={{
                    background: groupBackgroundGradient(group, '160deg'),
                  }}
                  aria-hidden
                />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">{group.name}</h2>
                  <p className="gallery-group-meta">{group.badges.length} badges</p>
                </div>
                <GalleryGroupRarityCounts badgeIds={group.badges.map((badge) => badge.id)} badgesMap={badgesMap} />
              </header>
              <div className="gallery-badge-grid">{group.badges.map(renderGalleryBadge)}</div>
            </div>
          </section>
        ))}

        {showFreeform ? (
          <section
            id="gallery-freeform"
            className="gallery-group gallery-group--freeform"
            ref={(el) => {
              sectionRefs.current.set('freeform', el);
            }}
          >
            <div className="gallery-group-content">
              <header className="gallery-group-head">
                <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">More Badges</h2>
                  <p className="gallery-group-meta">{freeform.length} badges</p>
                </div>
                <GalleryGroupRarityCounts badgeIds={freeform.map((badge) => badge.id)} badgesMap={badgesMap} />
              </header>
              <div className="gallery-badge-grid">{freeform.map(renderGalleryBadge)}</div>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};
